import React from "react";
import "./DataTable.css";

/**
 * Reusable DataTable
 *
 * Props:
 * - columns: Array<{
 *     header: string;
 *     key?: string;           // Key used to read/write editRows
 *     accessor?: (row) => any; // Custom display logic; defaults to row[key]
 *     editable?: boolean;      // Renders input when row is in edit mode
 *     inputProps?: object;     // Extra props for editable input (type, maxLength, etc.)
 *     className?: string;      // Optional CSS class on <th> and <td>
 *     renderCell?: (displayValue, row, idx) => ReactNode // Fully custom cell renderer
 *   }>
 *
 * - data: Array<object>
 * - showEditCols: boolean  — shows Update checkbox column only
 * - showDeleteCols: boolean — shows Delete checkbox column only
 * - editRows: { [rowIndex]: object }
 * - onToggleEditRow: (idx, row) => void
 * - onChangeEditCell: (idx, key, value) => void
 * - selectedForDelete: { [rowIndex]: boolean }
 * - onToggleDeleteRow: (idx) => void
 * - emptyMessage?: string
 * - rowKey?: (row, idx) => string | number
 * - className?: string        // Extra class on the wrapper
 */
export default function DataTable({
  columns = [],
  data = [],
  showEditCols = false,
  showDeleteCols = false,
  separateCols = false,
  editRows = {},
  onToggleEditRow = () => {},
  onChangeEditCell = () => {},
  selectedForDelete = {},
  onToggleDeleteRow = () => {},
  onSelectAllDelete = null,
  emptyMessage = "No data found",
  rowKey = (_row, idx) => idx,
  className = "",
  footerRow = null,
}) {
  const showDeleteCol = separateCols ? showDeleteCols : (showEditCols || showDeleteCols);
  const totalCols = columns.length + (showEditCols ? 1 : 0) + (showDeleteCol ? 1 : 0);

  const renderCell = (row, idx, col) => {
    const isEditing = Boolean(editRows[idx]);

    const displayValue =
      typeof col.accessor === "function"
        ? col.accessor(row)
        : col.key
        ? row[col.key]
        : "";

    // Custom renderer takes full control
    if (typeof col.renderCell === "function") {
      return col.renderCell(displayValue, row, idx);
    }

    if (isEditing && col.editable && col.key) {
      if (typeof col.renderEdit === "function") {
        return col.renderEdit(idx, editRows, onChangeEditCell);
      }
      const value = editRows[idx]?.[col.key] ?? "";
      return (
        <input
          className="dt-edit-input"
          value={value}
          onChange={(e) => onChangeEditCell(idx, col.key, e.target.value)}
          {...(col.inputProps || {})}
        />
      );
    }

    return displayValue ?? "";
  };

  return (
    <div className={`dataTableContainer ${className}`.trim()}>
      <table className="dataTable">
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={`h-${i}`} className={c.className || ""}>
                {c.header}
              </th>
            ))}
            {showEditCols && <th>Update</th>}
            {showDeleteCol && (
              <th>
                Delete
                {onSelectAllDelete && data.length > 0 && (
                  <input
                    type="checkbox"
                    style={{ marginLeft: 4, cursor: 'pointer' }}
                    checked={data.length > 0 && data.every((_, i) => selectedForDelete[i])}
                    onChange={(e) => onSelectAllDelete(e.target.checked)}
                    title={data.every((_, i) => selectedForDelete[i]) ? 'Deselect all' : 'Select all'}
                  />
                )}
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {Array.isArray(data) && data.length > 0 ? (
            data.map((row, idx) => {
              const isEditing = Boolean(editRows[idx]);
              return (
                <tr key={rowKey(row, idx)}>
                  {columns.map((col, ci) => (
                    <td key={`c-${idx}-${ci}`} className={col.className || ""} style={typeof col.cellStyle === 'function' ? col.cellStyle(row) : (col.cellStyle || {})}>
                      {renderCell(row, idx, col)}
                    </td>
                  ))}

                  {showEditCols && (
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={isEditing}
                        onChange={() => onToggleEditRow(idx, row)}
                      />
                    </td>
                  )}
                  {showDeleteCol && (
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedForDelete[idx] || false}
                        onChange={() => onToggleDeleteRow(idx)}
                      />
                    </td>
                  )}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={totalCols}
                style={{ textAlign: "center", padding: "16px", color: "#666" }}
              >
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>

        {footerRow && (
          <tfoot>
            <tr style={{ backgroundColor: "#6d6d6d" }}>
              {footerRow.map((val, i) => (
                <td key={`f-${i}`} style={{ fontWeight: "600", color: "#fff", textAlign: "center" }}>
                  {val ?? ""}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
